import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import {
  BacktestingJobResults,
  BacktestingStatus,
  BotData,
  RawLineChartDataPoint,
} from "@shared/types";
import { useJobQuery } from "@shared/services/jobs/hooks";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { useSelector } from "react-redux";

interface useBacktestingDrawerProps {
  backtestingBotId?: number;
  backtestingId?: number;
}

const useBacktestingDrawer = ({
  backtestingBotId,
  backtestingId,
}: useBacktestingDrawerProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const getBotDataArgs = useMemo(
    () =>
      backtestingBotId
        ? {
            dca_bot_id: backtestingBotId,
            is_backtesting: true,
            is_channel_configuration: false,
          }
        : skipToken,
    [backtestingBotId]
  );
  const { data, currentData: botCurrentData } =
    sharedLib.sharedApi.bots.useGetBotDataQuery(getBotDataArgs);

  const getBacktestingRunArgs = useMemo(
    () => (backtestingId ? { backtesting_run_id: backtestingId } : skipToken),
    [backtestingId]
  );
  const { currentData: backtestingRun } =
    sharedLib.sharedApi.bots.useGetBacktestingRunQuery(getBacktestingRunArgs);

  const botData = data?.data;
  const botDataToUse = useMemo(
    () =>
      botData && backtestingRun?.realised_pnl
        ? ({
            ...botData,
            change: [
              backtestingRun?.realised_pnl.USD,
              backtestingRun?.realised_pnl.BTC,
            ],
          } as BotData)
        : botData,
    [botData, backtestingRun]
  );

  const loading = !botCurrentData || !backtestingRun;

  const isOriginallyInProgress =
    (backtestingRun?.original_status ?? backtestingRun?.status) ===
    BacktestingStatus.IN_PROGRESS;
  const jobQueryStatus = useJobQuery(
    isOriginallyInProgress ? backtestingRun?.job_id : undefined,
    true
  );
  const backtestingJobResults = jobQueryStatus?.data
    ?.data as BacktestingJobResults;

  const barChartData = backtestingJobResults?.profit_by_day_graph_data;
  const parsedBarChartData = useMemo(() => {
    return (barChartData?.data?.start || []).map((dataPoint) => {
      return [dataPoint[0], ...dataPoint[1]];
    }) as RawLineChartDataPoint[];
  }, [barChartData]);

  const liveBarChartData = useMemo(() => {
    const precisionConfig = barChartData?.precision;
    const precision =
      displayCurrency === DISPLAY_CURRENCIES.BTC
        ? precisionConfig?.btc_amount
        : precisionConfig?.usd_amount;
    const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
    return formatTimeSeriesChartData(
      parsedBarChartData,
      yValueIndex,
      false,
      undefined,
      precision || 0
    );
  }, [barChartData, parsedBarChartData]);

  return {
    botData: botDataToUse,
    loading,
    backtestingRun,
    backtestingJobResults,
    liveBarChartData,
  };
};

export default useBacktestingDrawer;
